import "./StainOrderDialog.css";

import { DateTime } from "luxon";

import { Case, LabOrder, Stain } from "pwapi";

import { StainList } from "custom-models/frontLoader";

import { cancelLabOrder } from "shared/actions/labOrdersActions";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";
import selectLabStains from "shared/selectors/selectLabStains";
import selectSortedCaseDetail from "shared/selectors/selectSortedCaseDetail";

import { useAppDispatch, useAppSelector } from "store/hooks";

import StainInputGroup from "./dialog-inputs/StainInputGroup";
import Sidebar from "./Sidebar";
import StainOrderGroup from "./StainOrderDialog/StainOrderGroup";

interface StainOrderDialogProps {
  slideDetailObject: SlideDetailState;
  setDialog?: Function;
}

export type ActiveLabOrders = {
  [key: string]: LabOrder[];
};

export type BlockDetails = {
  specimen?: number;
  block?: string | null;
  slide: number;
};

const StainOrderDialog = ({
  slideDetailObject,
  setDialog,
}: StainOrderDialogProps) => {
  const dispatch = useAppDispatch();
  const sortedCase = useAppSelector(selectSortedCaseDetail);

  const features: FeatureFlags = useAppSelector(({ sys }) => sys.features);
  const availableLabStains = useAppSelector((state) =>
    selectLabStains(state, sortedCase.accessioning_lab?.id || 0)
  );
  const labStains = useAppSelector(({ labStains }) => labStains);
  const stains = useAppSelector(({ stains }) => stains);

  const { blocks, slides } = sortedCase;
  const filteredLabOrders = (sortedCase.lab_orders || []).filter((labOrder) => {
    const stain: StainList | undefined =
      stains[labStains[labOrder.lab_stain_id || 0].stain_id || 0];
    const isUnstained =
      stain?.abbreviation === "UNSTN" || stain?.name === "Unstained";

    if (
      labOrder.slide_id &&
      !sortedCase.slides.some((s) => s.id === labOrder.slide_id)
    )
      return false;

    if (
      !labOrder.completed_at &&
      labOrder.cancelled_at &&
      labOrder.transmitting_at &&
      DateTime.fromISO(labOrder.cancelled_at) <
        DateTime.fromISO(labOrder.transmitting_at)
    )
      return false;

    if (labOrder.user) return true;
    if (labOrder.lab_panel_id) return true;

    if (stain?.category === Stain.category.ROUTINE) return false;
    if (isUnstained) return false;

    return true;
  });

  const filteredBlocks = blocks.filter((block) =>
    filteredLabOrders.some((lab_order) => {
      const slideBlockId = slides.find(
        (s) => s.id === lab_order.slide_id
      )?.block_obj_id;
      if (block.id === slideBlockId) return true;
      return lab_order.block_id === block.id;
    })
  );

  const onCancelOrder = (order: LabOrder) => {
    order.id && dispatch(cancelLabOrder({ id: order.id }));
  };

  return (
    <Sidebar isWide>
      <div
        className="StainOrderDialog Sidebar__contentContainer"
        data-testid="StainOrderDialog"
      >
        {!filteredLabOrders.length && !sortedCase.blocks?.length ? (
          <dl>
            <dt>There are no Blocks to place Orders on</dt>
          </dl>
        ) : (
          <>
            <dl>
              <dt>
                {filteredLabOrders.length ? (
                  <>Orders ({filteredLabOrders.length})</>
                ) : (
                  <>There are no active orders</>
                )}
              </dt>
              <dd>
                {filteredBlocks.map((block) => (
                  <StainOrderGroup
                    groupLabel={block.full_name}
                    group={filteredLabOrders.filter((labOrder) => {
                      const slideBlockId = slides.find(
                        (s) => s.id === labOrder.slide_id
                      )?.block_obj_id;
                      if (block.id === slideBlockId) return true;
                      return labOrder.block_id === block.id;
                    })}
                    onCancelOrder={onCancelOrder}
                    currentBlock={slideDetailObject.block_obj_id === block.id}
                    key={block.id}
                    hideStatusIcons={sortedCase?.source === Case.source.WPL}
                  />
                ))}
              </dd>
            </dl>
            {features?.stainOrdering && (
              <StainInputGroup
                slideDetail={slideDetailObject}
                stainCatalog={availableLabStains}
                setDialog={setDialog}
              />
            )}
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default StainOrderDialog;
